import { useEffect, useState } from 'react';
import ChargerModule from '../../../components/chargerModule';
import useDebounce from '../../../hooks/useDebounce';
import { activatePaymentAgreement, deactivateCredit, getBussinesList } from '../../../services/bussines';
import { IBussinesDetail, TYPE_PAYMENT_AGREEMENT, UUID_PAYMENT_AGREEMENT } from '../../../types/bussines';
import errorMessage from '../../../utils/errorMessage';
import { BussinesFilters, CardBussines, HeaderBussines, LoaderBussines, ModalActivateCredit } from './components';
import searchIcon from '../../../assets/images/catalogue/searchIcon.png';
import { getNudosData } from '../../../utils/getNudosData';
import useFiltersState from '../../../state/useFiltersState';
import { ImoduleFilter } from '../../../types/global';
import { displaySuccessNotification } from '../../../utils/displayNudosStandardNotifications';
import BordModalConfirm from '../../../components/NudosComponents/BordModalConfirm/BordModalConfirm';
import './Bussines.scss';

const Bussines = () => {
  const [searchText, setSearchText] = useState<string>('');
  const [dataBusiness, setDataBusiness] = useState<IBussinesDetail[]>([]);
  const [organizationId, setOrganizationId] = useState<number>();
  const [loadingActivate, setLoadingActivate] = useState<boolean>(false);
  const [showModalConfirmation, setShowModalConfirmation] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [isEditPaymentAgreement, setIsEditPaymentAgreement] = useState<boolean>(false);
  const [typePaymentAgreement, setTypePaymentAgreement] = useState<UUID_PAYMENT_AGREEMENT>(
    UUID_PAYMENT_AGREEMENT.REQUIRE_PAYMENT_AGREEMENT_FOR_SERVICE
  );
  const infoSideBar = { position: 'organization' };
  const headerInfo = { title: 'Empresas', subtitle: '' };
  const { bussinesFilters } = useFiltersState();
  const productDebounce = useDebounce(searchText, 1000);
  const [showModalInactivePaymentAgreement, setShowModalInactivePaymentAgreement] = useState<boolean>(false);

  const getData = async (search?: string, noLoading?: boolean, bussinesFilters?: ImoduleFilter[]) => {
    if (!noLoading) {
      setLoading(true);
    }
    const searchIdentifier = bussinesFilters && bussinesFilters?.length > 0 && search ? '&' : '?';

    const filters =
      bussinesFilters && bussinesFilters.length > 0
        ? `${searchIdentifier}filters=${JSON.stringify(bussinesFilters)}`
        : '';
    try {
      const data = await getBussinesList(search, filters);
      setDataBusiness([...data]);
      setLoading(false);
    } catch (error) {
      errorMessage();
      setLoading(false);
    }
  };

  const openCreditModal = (id: number, isEdit?: boolean) => {
    setOrganizationId(id);
    setIsEditPaymentAgreement(isEdit || false);
  };

  const handleActivatePaymentAgreement = async (days: string | number, type: TYPE_PAYMENT_AGREEMENT) => {
    setLoadingActivate(true);
    if (days && organizationId) {
      const body = {
        organizationId,
        commercialPreferenceValue: String(days),
        commercialPreferenceTypeUuid:
          type === TYPE_PAYMENT_AGREEMENT.ORDER
            ? UUID_PAYMENT_AGREEMENT.REQUIRE_PAYMENT_AGREEMENT_FOR_ORDER
            : UUID_PAYMENT_AGREEMENT.REQUIRE_PAYMENT_AGREEMENT_FOR_SERVICE
      };
      try {
        await activatePaymentAgreement(body);
        await getData(productDebounce || '', true, bussinesFilters);
        setLoadingActivate(false);
        setOrganizationId(undefined);
        displaySuccessNotification({ customJSXMessage: <>Activaste el acuerdo de pago exitosamente</> });
      } catch (error) {
        errorMessage();
        setLoadingActivate(false);
      }
    } else {
      setLoadingActivate(false);
    }
  };

  const handleTypePaymentAgreement = async (type: TYPE_PAYMENT_AGREEMENT, deadline: number | string) => {
    switch (type) {
      case TYPE_PAYMENT_AGREEMENT.ORDER:
        await handleActivatePaymentAgreement(deadline, TYPE_PAYMENT_AGREEMENT.ORDER);
        break;
      case TYPE_PAYMENT_AGREEMENT.SERVICE:
        await handleActivatePaymentAgreement(deadline, TYPE_PAYMENT_AGREEMENT.SERVICE);
        break;
      case TYPE_PAYMENT_AGREEMENT.BOTH:
        await Promise.all([
          handleActivatePaymentAgreement(deadline, TYPE_PAYMENT_AGREEMENT.ORDER),
          handleActivatePaymentAgreement(deadline, TYPE_PAYMENT_AGREEMENT.SERVICE)
        ]);
        break;
      default:
        break;
    }
  };

  const closeModal = () => {
    setShowModalConfirmation(true);
  };

  const closeModalConfirmation = () => {
    setOrganizationId(undefined);
    setShowModalConfirmation(false);
  };

  const closeModalInactivePaymentAgreement = () => {
    setShowModalInactivePaymentAgreement(false);
  };

  const handleContinueConfiguration = () => {
    setShowModalConfirmation(false);
  };

  const handleContinueInactivePaymentAgreement = async () => {
    if (!organizationId) return;
    setLoadingActivate(true);
    try {
      await deactivateCredit(organizationId, typePaymentAgreement);
      await getData(productDebounce || '', true, bussinesFilters);
      displaySuccessNotification({ customJSXMessage: <>Desactivaste el acuerdo de pago exitosamente</> });
    } catch {
      errorMessage();
    } finally {
      setShowModalInactivePaymentAgreement(false);
      setLoadingActivate(false);
    }
  };

  const getOrganizationNameById = (): string | undefined => {
    if (!organizationId) return undefined;
    const org = dataBusiness.find((item: any) => item.id === organizationId);
    return org?.name;
  };
  const organizationName = getOrganizationNameById();

  const handleDeactivateAgreementPayment = async (value: boolean, type: TYPE_PAYMENT_AGREEMENT) => {
    if (!organizationId) return;
    setShowModalInactivePaymentAgreement(value);

    if (type === TYPE_PAYMENT_AGREEMENT.ORDER) {
      setTypePaymentAgreement(UUID_PAYMENT_AGREEMENT.REQUIRE_PAYMENT_AGREEMENT_FOR_ORDER);
    } else {
      setTypePaymentAgreement(UUID_PAYMENT_AGREEMENT.REQUIRE_PAYMENT_AGREEMENT_FOR_SERVICE);
    }
  };

  useEffect(() => {
    getData(productDebounce, false, bussinesFilters);
  }, [productDebounce, bussinesFilters]);

  return (
    <ChargerModule
      headerInfo={headerInfo}
      infoSideBar={infoSideBar}
      viewSearchBar
      searchText={searchText}
      setSearchText={setSearchText}
    >
      {organizationId && !showModalConfirmation && !showModalInactivePaymentAgreement && (
        <ModalActivateCredit
          handleActivatePaymentAgreement={handleTypePaymentAgreement}
          closeModal={closeModal}
          loadingActivate={loadingActivate}
          organizationName={organizationName}
          isEditPaymentAgreement={isEditPaymentAgreement}
          handleDeactivateAgreementPayment={handleDeactivateAgreementPayment}
          onCancel={closeModalConfirmation}
          dataBusiness={dataBusiness}
          organizationId={organizationId}
        />
      )}
      {showModalConfirmation && (
        <BordModalConfirm
          onAccept={handleContinueConfiguration}
          onCancel={closeModalConfirmation}
          isLoading={loadingActivate}
          isDisabled={false}
          title="No puedes actualizar el acuerdo de pago para este cliente sin incluir el plazo, ¿deseas salir de todas formas?"
          textButtonAccept="Continuar configuración"
          textButtonCancel="Salir sin guardar"
          customSizeIcon={48}
        />
      )}
      {showModalInactivePaymentAgreement && (
        <BordModalConfirm
          onAccept={handleContinueInactivePaymentAgreement}
          onCancel={closeModalInactivePaymentAgreement}
          isLoading={loadingActivate}
          isDisabled={false}
          title="Estás a punto de desactivar el acuerdo de pago para esta empresa. Al hacerlo, no se confirmarán automáticamente sus ordenes y servicios. ¿Seguro que deseas continuar?"
          textButtonAccept="Si, desactivar"
          textButtonCancel="No, cancelar"
          customSizeIcon={48}
        />
      )}
      <div id="bussines">
        <BussinesFilters />
        <div className="containerHeader">
          <HeaderBussines />
        </div>
        <div className="containerCards">
          {loading ? (
            <LoaderBussines />
          ) : (
            <>
              {dataBusiness?.length === 0 ? (
                <div className="notFound">
                  <div>
                    <div className="centerItems imgSpace">
                      <img className="sizeImg" alt="notFoundImg" src={searchIcon} />
                    </div>
                    <div className="centerItems">
                      <div className="textNoFound">No hay resultados para tu busqueda</div>
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  {dataBusiness?.map((item, index) => {
                    return (
                      <CardBussines
                        key={`card-bussines-${item?.id}-index-${index}`}
                        dataCard={item}
                        openCreditModal={openCreditModal}
                      />
                    );
                  })}
                </>
              )}
            </>
          )}
        </div>
      </div>
    </ChargerModule>
  );
};

export default Bussines;
